import { DateTime } from 'luxon';
import { CheckBox, CheckBoxOutlineBlank, Delete, Edit } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Role } from 'dtos/users/common';
import { UserAdminOutput } from 'dtos/users/UserAdminOutput';
import { UserOutput } from 'dtos/users/UserOutput';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { RouteName } from 'stores/RouteStore';
import { UserListStore } from 'stores/users/UserListStore';

function renderDate(date: string): string {
  return DateTime.fromISO(date).toLocal().toFormat('yyyy-MM-dd HH:mm');
}

type UserListProps = {
  currentUser?: UserOutput;
  userListStore: UserListStore;
  navigateByName: (route: RouteName, params?: any) => void;
};

const UserList = observer(({ currentUser, userListStore, navigateByName }: UserListProps) => {
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<UserAdminOutput | undefined>(undefined);
  const { users, deleteUser } = userListStore;

  const confirmDelete = (user: UserAdminOutput) => () => {
    setConfirmDeleteUser(user);
  };

  const handleDeleteUserClick = (user: UserAdminOutput) => () => {
    setConfirmDeleteUser(undefined);
    deleteUser(user);
  };

  return (
    <div aria-label="edit users page" style={{ width: 600, marginTop: 60, marginBottom: 30 }}>
      <Stack spacing={4}>
        <Typography variant="h5">
          <strong>Edit Users</strong>
        </Typography>
        <Table sx={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, background: 'white' }} variant="head">
                Username
              </TableCell>
              <TableCell variant="head">Email</TableCell>
              <TableCell variant="head">Created</TableCell>
              <TableCell variant="head">Updated</TableCell>
              <TableCell variant="head">Source</TableCell>
              <TableCell variant="head" align="center">
                Enabled
              </TableCell>
              <TableCell variant="head" align="center">
                Internal
              </TableCell>
              <TableCell variant="head" align="center">
                Admin
              </TableCell>
              <TableCell variant="head" align="center">
                Edit
              </TableCell>
              <TableCell variant="head" align="center">
                Delete
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.username}>
                <TableCell sx={{ position: 'sticky', left: 0, background: 'white' }} variant="body">
                  {user.username}
                </TableCell>
                <TableCell variant="body">{user.email}</TableCell>
                <TableCell variant="body">{user.created && renderDate(user.created)}</TableCell>
                <TableCell variant="body">{user.updated && renderDate(user.updated)}</TableCell>
                <TableCell variant="body">{user.source}</TableCell>
                <TableCell variant="body" align="center">
                  {user.roles.includes(Role.USER) ? (
                    <CheckBox color="disabled" />
                  ) : (
                    <CheckBoxOutlineBlank color="disabled" />
                  )}
                </TableCell>
                <TableCell variant="body" align="center">
                  {user.roles.includes(Role.INTERNAL) ? (
                    <CheckBox color="disabled" />
                  ) : (
                    <CheckBoxOutlineBlank color="disabled" />
                  )}
                </TableCell>
                <TableCell variant="body" align="center">
                  {user.roles.includes(Role.ADMIN) ? (
                    <CheckBox color="disabled" />
                  ) : (
                    <CheckBoxOutlineBlank color="disabled" />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => navigateByName('editUser', { username: user.username })}>
                    <Edit />
                  </IconButton>
                </TableCell>
                <TableCell>
                  {user.username !== currentUser?.username && (
                    <IconButton onClick={confirmDelete(user)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>

      {confirmDeleteUser && (
        <Dialog open onClose={() => setConfirmDeleteUser(undefined)} aria-label="confirm delete">
          <DialogContent>
            <DialogContentText>Delete user {confirmDeleteUser.username}?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={() => setConfirmDeleteUser(undefined)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteUserClick(confirmDeleteUser)}>Delete</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
});

export { UserList };
